import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  message,
  Modal,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../components/common/FilterPanel';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';

import {
  OperatorGroup,
  OperatorGroupDTO,
  OperatorGroupFilters,
  OperatorGroupStatus,
  PageOperatorGroupRequest,
  Division,
  Worker,
} from '../../types/operatorGroup';
import operatorGroupService from '../../services/operatorGroupService';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import '../../styles/operatorGroup.css';

const { Option } = Select;
const { confirm } = Modal;

const RESPONSIBLE_TEAM_OPTIONS: { value: any; label: string }[] = [
  { value: 'Planogram Team', label: 'Planogram Team' },
  { value: 'Construction Team', label: 'Construction Team' },
  { value: 'Commercial Team', label: 'Commercial Team' },
  { value: 'Marketing Team', label: 'Marketing Team' },
  { value: 'Operation Team', label: 'Operation Team' },
  { value: 'IT Team', label: 'IT Team' },
];

const OperatorGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleAsyncOperation, showError, showSuccess, isLoading } = useErrorHandling();
  
  // 状态管理
  const [dataSource, setDataSource] = useState<OperatorGroupDTO[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // 筛选条件
  const [filters, setFilters] = useState<OperatorGroupFilters>({});

  // 初始化加载
  useEffect(() => {
    loadData();
  }, []);

  // 加载操作组数据
  const loadData = async (page = 1, pageSize = 20, customFilters?: OperatorGroupFilters) => {
    const query: PageOperatorGroupRequest = {
      currentPage: page,
      pageSize,
      ...customFilters,
    };

    const response = await handleAsyncOperation(
      () => operatorGroupService.pageQueryOperatorGroups(query),
      {
        onSuccess: (data) => {
          if (data && data.data) {
            setDataSource(data.data.tableData);
            setPagination({
              current: page,
              pageSize,
              total: data.data.paging.totalCount,
            });
          }
        },
        onError: (error) => {
          console.error('加载操作组数据失败:', error);
          message.error(t('operatorGroup.messages.loadFailed'));
        }
      }
    );
  };

  // 搜索
  const handleSearch = (values: any) => {
    const searchFilters: OperatorGroupFilters = {
      ...values,
      dateRange: values.dateRange 
        ? [
            values.dateRange[0]?.startOf('day').valueOf(),
            values.dateRange[1]?.endOf('day').valueOf()
          ]
        : undefined,
    };
    
    setFilters(searchFilters);
    loadData(1, pagination.pageSize, searchFilters);
  };

  // 重置搜索
  const handleReset = () => {
    setFilters({});
    loadData(1, pagination.pageSize, {});
  };

  // 刷新数据
  const handleRefresh = () => {
    loadData(pagination.current, pagination.pageSize, filters);
  };

  // 表格变更处理
  const handleTableChange = (paginationConfig: any, tableFilters: any, sorter: any) => {
    const { current, pageSize } = paginationConfig;
    
    // 更新分页信息
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    // 处理排序
    if (sorter.field && sorter.order) {
      setFilters(prev => ({
        ...prev,
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
      }));
    } else if (filters.sortBy || filters.sortOrder) {
      // 如果之前有排序，但现在没有了，需要清除排序条件
      const newFilters = { ...filters };
      delete newFilters.sortBy;
      delete newFilters.sortOrder;
      setFilters(newFilters);
    }
  };

  // 本地排序处理
  const sortedDataSource = useMemo(() => {
    if (!filters.sortBy || !filters.sortOrder) {
      return dataSource;
    }
    
    const { sortBy, sortOrder } = filters;
    return [...dataSource].sort((a, b) => {
      const aValue = a[sortBy as keyof OperatorGroupDTO];
      const bValue = b[sortBy as keyof OperatorGroupDTO];
      
      // 处理不同数据类型的比较
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? result : -result;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortOrder === 'asc' ? result : -result;
      }
      
      // 对于其他类型，转换为字符串进行比较
      const result = String(aValue).localeCompare(String(bValue));
      return sortOrder === 'asc' ? result : -result;
    });
  }, [dataSource, filters.sortBy, filters.sortOrder]);

  // 选择变更处理
  const handleSelectionChange = (keys: (string | number)[]) => {
    setSelectedRowKeys(keys);
  };

  // 查看详情
  const handleView = (record: OperatorGroupDTO) => {
    navigate(`/operator-group/detail/${record.code}`);
  };

  // 编辑
  const handleEdit = (record: OperatorGroupDTO) => {
    navigate(`/operator-group/edit/${record.code}`);
  };

  // 批量状态更新
  const handleBatchStatusChange = async (status: OperatorGroupStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('operatorGroup.messages.pleaseSelectRows'));
      return;
    }

    const selectedGroups = dataSource.filter(item => selectedRowKeys.includes(item.code));
    const actionText = status === 'ENABLE' ? t('operatorGroup.batch.enable') : t('operatorGroup.batch.disable');
    const confirmTitle = status === 'ENABLE' ? t('operatorGroup.batch.enableConfirm') : t('operatorGroup.batch.disableConfirm');
    const confirmContent = status === 'ENABLE' ? t('operatorGroup.batch.enableContent') : t('operatorGroup.batch.disableContent');
    const confirmOk = status === 'ENABLE' ? t('operatorGroup.batch.confirmEnable') : t('operatorGroup.batch.confirmDisable');
    const groupNames = selectedGroups.map(item => item.name).join('、');

    confirm({
      title: confirmTitle,
      content: `${confirmContent}\n${groupNames}`,
      okText: confirmOk,
      cancelText: t('operatorGroup.cancel'),
      width: 520,
      onOk: async () => {
        const result = await handleAsyncOperation(
          () => operatorGroupService.batchUpdateOperatorGroupStatus({
            codes: selectedRowKeys as string[],
            status: status
          }),
          {
            onSuccess: (data) => {
              if (data.success) {
                const successMessage = status === 'ENABLE' ? t('operatorGroup.batch.enableSuccess') : t('operatorGroup.batch.disableSuccess');
                showSuccess(successMessage);
                // 清空选择并刷新数据
                setSelectedRowKeys([]);
                handleRefresh();
              }
            },
            onError: (error) => {
              console.error(`批量${actionText}操作组失败:`, error);
              const errorMessage = status === 'ENABLE' ? t('operatorGroup.batch.enableFailed') : t('operatorGroup.batch.disableFailed');
              message.error(errorMessage);
            }
          }
        );
      },
    });
  };

  // 新建
  const handleCreate = () => {
    navigate('/operator-group/create');
  };

  // 表格列定义
  const columns = [
    {
      title: t('operatorGroup.table.columns.code'),
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      width: 140,
      render: (text: string, record: OperatorGroupDTO) => (
        <a onClick={() => handleView(record)}>{text}</a>
      ),
    },
    {
      title: t('operatorGroup.table.columns.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: t('operatorGroup.table.columns.division'),
      dataIndex: 'responsibleTeam',
      key: 'responsibleTeam',
      sorter: true,
      width: 150,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: t('operatorGroup.table.columns.director'),
      dataIndex: 'director',
      key: 'director',
      width: 100,
    },
    {
      title: t('operatorGroup.table.columns.workersCount'),
      dataIndex: 'workers',
      key: 'workers',
      width: 100,
      align: 'center' as const,
      render: (workers: Worker[]) => <Tag color="green">{workers?.length || 0}{t('operatorGroup.detail.personLabel')}</Tag>,
    },
    {
      title: t('operatorGroup.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: OperatorGroupStatus) => {
        const config = {
          ENABLE: { color: 'green', text: t('operatorGroup.status.ENABLE') },
          DISABLE: { color: 'red', text: t('operatorGroup.status.DISABLE') },
        };
        const { color, text } = config[status] || { color: 'gray', text: t('operatorGroup.status.unknown') };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: t('operatorGroup.table.columns.creator'),
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 100,
    },
    {
      title: t('operatorGroup.table.columns.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      sorter: true,
      width: 150,
      render: (timestamp: number) => 
        timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: t('operatorGroup.table.columns.updateTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      sorter: true,
      width: 150,
      render: (timestamp: number) => 
        timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: t('operatorGroup.table.columns.actions'),
      key: 'action',
      fixed: 'right' as const,
      width: 160,
      render: (_: any, record: OperatorGroupDTO) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
            title={t('operatorGroup.table.actions.view')}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            title={t('operatorGroup.table.actions.edit')}
          />
        </Space>
      ),
    },
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectionChange,
  };

  return (
    <div>
      {/* 筛选面板 */}
      <FilterPanel
        onFilter={handleSearch}
        onReset={handleReset}
        title={t('menu.operatorGroup')}
        className="filter-card"
      >
        <Col span={6}>
          <Form.Item name="code" label={t('operatorGroup.code')}>
            <Input placeholder={t('operatorGroup.placeholders.code')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="name" label={t('operatorGroup.name')}>
            <Input placeholder={t('operatorGroup.placeholders.name')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="responsibleTeam" label={t('operatorGroup.division')}>
            <Select placeholder={t('operatorGroup.placeholders.division')} allowClear>
              {RESPONSIBLE_TEAM_OPTIONS.map(team => (
                <Option key={team.value} value={team.value}>
                  {team.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="dateRange" label={t('operatorGroup.createTime')}>
            <DatePicker.RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label={t('operatorGroup.table.columns.status')}>
            <Select placeholder={t('operatorGroup.placeholders.status')} allowClear>
              <Option value="ENABLE">{t('operatorGroup.status.ENABLE')}</Option>
              <Option value="DISABLE">{t('operatorGroup.status.DISABLE')}</Option>
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card>
        {/* 批量操作工具栏 */}
        
        
        {/* 操作区域 */}
        <div className="action-panel">
          <Row justify="space-between" align="middle">
            <BatchActionToolbar
              selectedCount={selectedRowKeys.length}
              actions={[
                {
                  key: 'batchEnable',
                  icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                  label: t('operatorGroup.batch.enable'),
                  onClick: () => handleBatchStatusChange('ENABLE'),
                  disabled: isLoading
                },
                {
                  key: 'batchDisable',
                  icon: <StopOutlined style={{ color: '#faad14' }} />,
                  label: t('operatorGroup.batch.disable'),
                  onClick: () => handleBatchStatusChange('DISABLE'),
                  disabled: isLoading
                }
              ]}
              onClearSelection={() => setSelectedRowKeys([])}
            />

            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  {t('operatorGroup.create')}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>


        {/* 表格区域 */}
        <div className="table-container">
          <Spin spinning={isLoading}>
            <Table
              columns={columns}
              dataSource={sortedDataSource}
              rowKey="id"
              rowSelection={rowSelection}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  t('operatorGroup.pagination.total', {
                    total,
                    start: range[0],
                    end: range[1]
                  }),
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200, y: 500 }}
              size="middle"
            />
          </Spin>
        </div>
      </Card>
    </div>
  );
};

export default OperatorGroupList;